import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import s from "./Candidate.module.css";
import type { AnswerState, Identification, Phase, Question, StartResponse } from "../lib/types";
import { assessmentStart, answerAutosave, assessmentSubmit, uploadArtifact } from "../lib/api";
import { attachTelemetry, type TelemetryEvent } from "../lib/telemetry";
import { ensureMicPermission, startRecorder, type Recorder } from "../lib/audio";

/* ---------- markdown leve (negrito, code, fences) ---------- */
function inline(seg: string, base: number) {
  const nodes: React.ReactNode[] = [];
  const re = /(\*\*([^*]+)\*\*|`([^`]+)`)/g;
  let last = 0, m: RegExpExecArray | null, k = base;
  while ((m = re.exec(seg))) {
    if (m.index > last) nodes.push(seg.slice(last, m.index));
    if (m[2] !== undefined) nodes.push(<strong key={k++}>{m[2]}</strong>);
    else nodes.push(<code key={k++}>{m[3]}</code>);
    last = m.index + m[0].length;
  }
  if (last < seg.length) nodes.push(seg.slice(last));
  return nodes;
}
function renderPrompt(text: string) {
  return text.split("```").map((seg, i) =>
    i % 2 === 1
      ? <pre key={i}>{seg.replace(/^[a-zA-Z]+\n/, "")}</pre>
      : <span key={i}>{inline(seg, i * 1000)}</span>
  );
}
function fmt(sec: number) {
  const m = Math.floor(sec / 60), x = Math.max(0, Math.floor(sec % 60));
  return `${m}:${String(x).padStart(2, "0")}`;
}
const LS = (token: string) => `rec_session_${token}`;

/* uma questão está respondida se tem texto, escolha ou artefato (áudio/arquivo) */
function isAnswered(a?: AnswerState): boolean {
  if (!a) return false;
  if (a.artifact) return true;
  if (typeof a.text === "string" && a.text.trim() !== "") return true;
  const c = a.choice;
  if (Array.isArray(c)) return c.length > 0;
  if (typeof c === "string") return c.trim() !== "";
  return false;
}

export function CandidateApp() {
  const { token = "" } = useParams<{ token: string }>();
  const [phase, setPhase] = useState<Phase>("loading");
  const [err, setErr] = useState<string | null>(null);
  const [start, setStart] = useState<StartResponse | null>(null);
  const sessionId = start?.session_id || "";
  const questions = useMemo<Question[]>(() => start?.questions || [], [start]);

  const [answers, setAnswers] = useState<Record<string, AnswerState>>({});
  const [idx, setIdx] = useState(0);
  const [ident, setIdent] = useState<Identification>({
    name: "", email: "", phone: "", city: "", pretensao: "", disponibilidade_iniciar: "",
    flags: { outro_processo: false, negocio_proprio: false, sem_disponibilidade: false },
  });
  const [micGranted, setMicGranted] = useState(false);
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState<"" | "saving" | "saved">("");

  const teleBuf = useRef<TelemetryEvent[]>([]);
  const qEnter = useRef<number>(Date.now());
  const saveTimer = useRef<number | undefined>(undefined);

  /* ---------- boot ---------- */
  const booted = useRef(false);
  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    (async () => {
      try {
        const r = await assessmentStart(token);
        if (r.already || r.status === "submitted" || r.status === "graded" || r.status === "reviewed") {
          setPhase("closed"); setStart(r); return;
        }
        setStart(r);
        // restaura local + respostas do servidor
        const local = JSON.parse(localStorage.getItem(LS(token)) || "null");
        const merged: Record<string, AnswerState> = {};
        for (const a of r.answers || []) merged[a.question_id] = {
          text: a.answer_text || undefined,
          choice: (a.answer_choice as any) || undefined,
        };
        if (local?.answers) for (const k of Object.keys(local.answers)) merged[k] = { ...merged[k], ...local.answers[k] };
        setAnswers(merged);
        if (r.identification) setIdent(r.identification);
        else if (local?.ident) setIdent(local.ident);
        if (local?.phase && local.phase !== "loading") { setPhase(local.phase); setIdx(local.idx || 0); }
        else setPhase("preflight");
      } catch (e) {
        setErr((e as Error).message); setPhase("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  /* ---------- telemetria ---------- */
  useEffect(() => {
    if (phase !== "running" && phase !== "identification") return;
    return attachTelemetry((e) => teleBuf.current.push({ ...e, question_id: questions[idx]?.id || null }));
  }, [phase, idx, questions]);

  /* ---------- persistência local ---------- */
  const persistLocal = useCallback((next?: Partial<{ answers: Record<string, AnswerState>; phase: Phase; idx: number; ident: Identification }>) => {
    const payload = {
      session_id: sessionId, phase: next?.phase ?? phase, idx: next?.idx ?? idx,
      answers: next?.answers ?? answers, ident: next?.ident ?? ident,
    };
    localStorage.setItem(LS(token), JSON.stringify(payload));
  }, [sessionId, phase, idx, answers, ident, token]);

  /* ---------- autosave servidor (debounce) ---------- */
  const flushSave = useCallback(async (q: Question, a: AnswerState) => {
    if (!sessionId) return;
    setSaving("saving");
    const tele = teleBuf.current.splice(0, teleBuf.current.length);
    try {
      await answerAutosave({
        session_id: sessionId, token, question_id: q.id,
        answer_text: a.text ?? null, answer_choice: a.choice ?? null,
        time_spent_sec: Math.round((Date.now() - qEnter.current) / 1000),
        telemetry: tele,
      });
      setSaving("saved");
    } catch { setSaving(""); }
  }, [sessionId, token]);

  const scheduleSave = useCallback((q: Question, a: AnswerState) => {
    window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => flushSave(q, a), 800);
  }, [flushSave]);

  const setAnswer = useCallback((q: Question, patch: Partial<AnswerState>) => {
    setAnswers((prev) => {
      const next = { ...prev, [q.id]: { ...prev[q.id], ...patch } };
      persistLocal({ answers: next });
      scheduleSave(q, next[q.id]);
      return next;
    });
  }, [persistLocal, scheduleSave]);

  /* ---------- timer por questão ---------- */
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (phase !== "running") return;
    qEnter.current = Date.now();
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [phase, idx]);

  if (phase === "loading") return <div className="splash">Carregando sua avaliação…</div>;
  if (phase === "error") return <Centered><h2>Não foi possível abrir</h2><p className={s.err}>{err}</p><p className={s.note}>Confira o link do convite ou fale com a Reach Up.</p></Centered>;
  if (phase === "closed") return <Centered><div className={s.bigemoji}>✓</div><h2>Avaliação já enviada</h2><p className={s.note}>Recebemos suas respostas. Boa sorte!</p></Centered>;
  if (phase === "done") return <Centered><div className={s.bigemoji}>✓</div><h2>Recebemos sua avaliação</h2><p className={s.note}>Obrigado por dedicar seu tempo. A Reach Up vai analisar e retornar. Boa sorte!</p></Centered>;

  /* ---------- C0 preflight ---------- */
  if (phase === "preflight") {
    return (
      <div className={s.wrap}>
        <Top title={start?.assessment.title} />
        <div className={s.center}>
          <div>
            <div className={s.kicker}>2ª etapa · processo seletivo</div>
            <h1>{start?.assessment.title}</h1>
          </div>
          <p className={s.note}>
            São {questions.length} questões práticas, <strong>todas obrigatórias</strong> — você só consegue enviar depois de responder todas.
            Algumas pedem <strong>áudio ao vivo</strong> (sua voz) e <strong>upload de arquivo</strong>.
            Cada questão tem um tempo recomendado visível. Suas respostas salvam sozinhas — se cair, é só reabrir o link.
          </p>
          <p className={s.note}>
            Para um processo justo, registramos eventos básicos da prova (colagens, troca de aba, tempos). Responda com suas próprias palavras —
            o que conta é o que <strong>você</strong> sabe fazer.
          </p>
          <label className={s.consent}>
            <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
            <span>Entendi e concordo com o registro de telemetria para garantir a integridade da avaliação.</span>
          </label>
          <button
            className="btn btn-primary btn-lg btn-block"
            disabled={!consent}
            onClick={async () => {
              const ok = await ensureMicPermission();
              setMicGranted(ok);
              setPhase("identification"); persistLocal({ phase: "identification" });
            }}
          >Começar</button>
          <p className={s.note}>Ao começar, vamos pedir permissão do microfone (para as questões de áudio).</p>
        </div>
      </div>
    );
  }

  /* ---------- C1 identificação ---------- */
  if (phase === "identification") {
    const valid = ident.name && ident.email && ident.phone;
    return (
      <div className={s.wrap}>
        <Top title={start?.assessment.title} />
        <div className={s.card}>
          <h2>Identificação</h2>
          <F label="Nome completo"><input value={ident.name} onChange={(e) => setIdent({ ...ident, name: e.target.value })} /></F>
          <F label="E-mail"><input type="email" value={ident.email} onChange={(e) => setIdent({ ...ident, email: e.target.value })} /></F>
          <F label="WhatsApp"><input value={ident.phone} onChange={(e) => setIdent({ ...ident, phone: e.target.value })} /></F>
          <F label="Cidade"><input value={ident.city} onChange={(e) => setIdent({ ...ident, city: e.target.value })} /></F>
          <F label="Pretensão salarial"><input value={ident.pretensao} onChange={(e) => setIdent({ ...ident, pretensao: e.target.value })} /></F>
          <F label="Disponibilidade para iniciar"><input value={ident.disponibilidade_iniciar} onChange={(e) => setIdent({ ...ident, disponibilidade_iniciar: e.target.value })} placeholder="ex.: imediata, 15 dias" /></F>
          <YN label="Você está em outro processo seletivo agora?" value={ident.flags.outro_processo} onChange={(v) => setIdent({ ...ident, flags: { ...ident.flags, outro_processo: v } })} />
          <YN label="Você tem um negócio próprio rodando?" value={ident.flags.negocio_proprio} onChange={(v) => setIdent({ ...ident, flags: { ...ident.flags, negocio_proprio: v } })} />
          <YN label="Tem disponibilidade após o horário comercial, quando necessário?" value={!ident.flags.sem_disponibilidade} invert onChange={(v) => setIdent({ ...ident, flags: { ...ident.flags, sem_disponibilidade: !v } })} />
        </div>
        <button
          className="btn btn-primary btn-lg btn-block"
          disabled={!valid}
          onClick={async () => {
            persistLocal({ phase: "running", ident });
            try { await answerAutosave({ session_id: sessionId, token, identification: ident }); } catch { /* fica no local */ }
            setPhase("running"); setIdx(0);
          }}
        >Iniciar a prova</button>
      </div>
    );
  }

  /* ---------- C3 revisão ---------- */
  if (phase === "review" || phase === "submitting") {
    const submitting = phase === "submitting";
    const missing = questions.filter((qq) => qq.is_required && !isAnswered(answers[qq.id]));
    const blocked = missing.length > 0;
    return (
      <div className={s.wrap}>
        <Top title={start?.assessment.title} />
        <div className={s.card}>
          <h2>Revisão</h2>
          <p className={s.note}>Confira antes de enviar. Você pode voltar e ajustar.</p>
          {questions.map((q, i) => {
            const a = answers[q.id];
            const ok = isAnswered(a);
            return (
              <div key={q.id} className={s.reviewItem}>
                <span>{q.order_index}. {(q.prompt.split("\n")[0] || "").slice(0, 40)}…</span>
                <span>
                  <span className={`badge ${ok ? "badge-success" : "badge-warning"}`}>{ok ? "respondida" : "obrigatória"}</span>{" "}
                  <button className="btn btn-ghost" style={{ padding: "2px 10px" }} onClick={() => { setIdx(i); setPhase("running"); }}>editar</button>
                </span>
              </div>
            );
          })}
        </div>
        {blocked && (
          <p className={s.err}>
            {missing.length === 1
              ? "Falta 1 questão obrigatória sem resposta. Toque em “editar” para responder antes de enviar."
              : `Faltam ${missing.length} questões obrigatórias sem resposta. Toque em “editar” em cada uma para responder antes de enviar.`}
          </p>
        )}
        <button
          className="btn btn-primary btn-lg btn-block"
          disabled={submitting || blocked}
          onClick={async () => {
            setPhase("submitting");
            try {
              await assessmentSubmit({ session_id: sessionId, token, identification: ident });
              localStorage.removeItem(LS(token));
              setPhase("done");
            } catch (e) { setErr((e as Error).message); setPhase("review"); }
          }}
        >{submitting ? "Enviando…" : "Enviar avaliação"}</button>
        {err && <p className={s.err}>{err}</p>}
      </div>
    );
  }

  /* ---------- C2 runner ---------- */
  const q = questions[idx];
  const a = answers[q.id] || {};
  const limit = q.config.time_limit_sec || q.config.audio_max_sec || 0;
  const elapsed = (now - qEnter.current) / 1000;
  const remaining = limit ? Math.max(0, limit - elapsed) : 0;

  return (
    <div className={s.wrap}>
      <Top title={start?.assessment.title}>
        {limit > 0 && <span className={`${s.timer} ${remaining <= 30 ? s.low : ""}`}>{remaining > 0 ? fmt(remaining) : "tempo recomendado esgotado"}</span>}
      </Top>
      <div className={s.progress}><i style={{ width: `${((idx + 1) / questions.length) * 100}%` }} /></div>
      <div className={s.card}>
        {q.section && <div className={s.section}>{q.section}</div>}
        <div className={s.kicker}>Questão {idx + 1} de {questions.length}</div>
        <div className={s.qprompt}>{renderPrompt(q.prompt)}</div>

        <QuestionInput
          q={q} a={a} micGranted={micGranted}
          onText={(t) => setAnswer(q, { text: t })}
          onChoice={(c) => setAnswer(q, { choice: c })}
          onArtifact={async (kind, file, name, duration) => {
            setSaving("saving");
            try {
              await uploadArtifact({ session_id: sessionId, token, question_id: q.id, kind, file, file_name: name, duration_sec: duration });
              setAnswer(q, { artifact: { kind, name, path: "ok" }, text: a.text });
              setSaving("saved");
            } catch (e) { setErr((e as Error).message); setSaving(""); }
          }}
        />
        <div className={saving === "saved" ? s.saved : s.saving}>{saving === "saved" ? "Salvo ✓" : saving === "saving" ? "Salvando…" : ""}</div>
      </div>

      <div className={s.nav}>
        <button className="btn btn-ghost" disabled={idx === 0} onClick={() => { setIdx(idx - 1); persistLocal({ idx: idx - 1 }); }}>Voltar</button>
        {idx < questions.length - 1
          ? <button className="btn btn-primary" onClick={() => { flushSave(q, answers[q.id] || {}); setIdx(idx + 1); persistLocal({ idx: idx + 1 }); }}>Próxima</button>
          : <button className="btn btn-primary" onClick={() => { flushSave(q, answers[q.id] || {}); setPhase("review"); persistLocal({ phase: "review" }); }}>Revisar</button>}
      </div>
    </div>
  );
}

/* ===================== sub-componentes ===================== */
function Top({ title, children }: { title?: string; children?: React.ReactNode }) {
  return (
    <div className={s.topbar}>
      <span className={s.brand}>Reach Up</span>
      {children || <span className={s.note} style={{ fontSize: 11 }}>{title}</span>}
    </div>
  );
}
function Centered({ children }: { children: React.ReactNode }) {
  return <div className={s.wrap}><div className={s.center} style={{ textAlign: "center", alignItems: "center" }}>{children}</div></div>;
}
function F({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className={s.field2}><label>{label}</label>{children}</div>;
}
function YN({ label, value, onChange, invert }: { label: string; value: boolean; onChange: (v: boolean) => void; invert?: boolean }) {
  return (
    <div className={s.field2}>
      <label>{label}</label>
      <div style={{ display: "flex", gap: 8 }}>
        <button type="button" className={`btn ${value ? "btn-primary" : "btn-ghost"}`} style={{ flex: 1 }} onClick={() => onChange(true)}>Sim</button>
        <button type="button" className={`btn ${!value ? "btn-primary" : "btn-ghost"}`} style={{ flex: 1 }} onClick={() => onChange(false)}>Não</button>
      </div>
    </div>
  );
}

function QuestionInput({ q, a, micGranted, onText, onChoice, onArtifact }: {
  q: Question; a: AnswerState; micGranted: boolean;
  onText: (t: string) => void; onChoice: (c: string | string[]) => void;
  onArtifact: (kind: "audio" | "file", file: File | Blob, name: string, duration?: number) => void;
}) {
  if (q.type === "mc_single" || q.type === "mc_multi") {
    const multi = q.type === "mc_multi";
    const sel: string[] = Array.isArray(a.choice) ? a.choice : a.choice ? [a.choice as string] : [];
    const toggle = (k: string) => {
      if (multi) { const next = sel.includes(k) ? sel.filter((x) => x !== k) : [...sel, k]; onChoice(next); }
      else onChoice(k);
    };
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {(q.config.options || []).map((o) => {
          const on = sel.includes(o.key);
          return (
            <div key={o.key} className={`${s.opt} ${multi ? "" : s.single} ${on ? s.sel : ""}`} onClick={() => toggle(o.key)} role="button" tabIndex={0}>
              <span className={s.mark}>{on ? "✓" : ""}</span><span>{o.label}</span>
            </div>
          );
        })}
      </div>
    );
  }
  if (q.type === "audio_response" || q.type === "defend_your_work") {
    if (!micGranted) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <p className={s.note}>Microfone indisponível. Responda por texto (registramos que o áudio não pôde ser usado).</p>
          <textarea className={s.ta} value={a.text || ""} onChange={(e) => onText(e.target.value)} placeholder="Sua resposta…" />
        </div>
      );
    }
    return <AudioRecorder maxSec={q.config.audio_max_sec || 60} done={!!a.artifact} onDone={(blob, mime, dur) => onArtifact("audio", new File([blob], `${q.id}.${mime.includes("mp4") ? "mp4" : "webm"}`, { type: mime }), `${q.id}.webm`, dur)} />;
  }
  if (q.type === "file_upload_deliverable") {
    return <FileUploader accept={(q.config.file_constraints?.accept || []).join(",")} done={a.artifact?.name} onFile={(f) => onArtifact("file", f, f.name)} />;
  }
  // scenario_open / debug_real / personalized_calc
  const txt = a.text || "";
  return (
    <div>
      <textarea className={s.ta} value={txt} onChange={(e) => onText(e.target.value)} placeholder="Escreva sua resposta…" />
      <div className={s.counter}>{txt.length} caracteres</div>
    </div>
  );
}

function AudioRecorder({ maxSec, onDone, done }: { maxSec: number; onDone: (b: Blob, mime: string, dur: number) => void; done: boolean }) {
  const [rec, setRec] = useState<Recorder | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [secs, setSecs] = useState(0);
  const [busy, setBusy] = useState(false);
  const tick = useRef<number | undefined>(undefined);

  const stop = useCallback(async () => {
    if (!rec) return;
    window.clearInterval(tick.current);
    setBusy(true);
    const out = await rec.stop();
    setRec(null);
    setUrl(URL.createObjectURL(out.blob));
    onDone(out.blob, out.mime, out.duration);
    setBusy(false);
  }, [rec, onDone]);

  useEffect(() => {
    if (rec && secs >= maxSec) stop();
  }, [secs, maxSec, rec, stop]);

  return (
    <div className={s.recorder}>
      {!rec && (
        <button type="button" className={`${s.recBtn}`} disabled={busy}
          onClick={async () => { try { const r = await startRecorder(); setRec(r); setSecs(0); tick.current = window.setInterval(() => setSecs((x) => x + 1), 1000); } catch { /* mic */ } }}>
          {url || done ? "Regravar" : "Gravar"}
        </button>
      )}
      {rec && <button type="button" className={`${s.recBtn} ${s.rec}`} onClick={stop}>Parar<br />{fmt(secs)}</button>}
      <p className={s.note}>Máx {maxSec}s · só sua voz, ao vivo {url || done ? "· gravado ✓" : ""}</p>
      {url && <div className={s.audioRow}><audio controls src={url} /></div>}
    </div>
  );
}

function FileUploader({ accept, onFile, done }: { accept: string; onFile: (f: File) => void; done?: string }) {
  const inp = useRef<HTMLInputElement>(null);
  return (
    <div>
      <div className={s.drop} onClick={() => inp.current?.click()}>
        {done ? <>Arquivo: <strong>{done}</strong><br /><span className={s.note}>Toque para trocar</span></> : <>Toque para anexar seu arquivo<br /><span className={s.note}>{accept || "qualquer formato"} · até 50 MB</span></>}
      </div>
      <input ref={inp} type="file" accept={accept} style={{ display: "none" }}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
    </div>
  );
}
